import { useLocalization } from '@lib/localization';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Card, CardContent, IconButton, InputBase, Pagination, Paper, Typography } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { useGetProjectQuery } from '@store/api/projectEndpoints';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InspectionRow from './InspectionRow';

interface InspectionsTabProps {
  projectId: string;
  inspections: IInspection[];
}

const ITEMS_PER_PAGE = 10;

/**
 * The project's inspections, as a list and nothing more.
 *
 * A row opens the inspection's own page; it used to unfold in place, which meant the tab
 * owned the selected inspection, its parsed file, the POI overlay and the viewer slice all
 * at once. All of that now belongs to the page the row links to, so nothing here downloads
 * an inspection file.
 */
const InspectionsTab = ({ projectId, inspections }: InspectionsTabProps) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { buildPath } = useProjectRoute();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Only for the report file's name, which reads better as the project's than as its id.
  const { data: project } = useGetProjectQuery({ id: projectId });

  const query = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const pagedInspections = useMemo(() => {
    const filtered = inspections.filter(inspection =>
      !query ||
      inspection.info.name.toLowerCase().includes(query) ||
      inspection.pointOfInterests?.some(
        poi =>
          poi.name.toLowerCase().includes(query) ||
          (poi.description ?? '').toLowerCase().includes(query)
      )
    );

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const data = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    return { filtered, totalPages, data };
  }, [inspections, query, page]);

  const openInspection = useCallback(
    (inspectionId: string) => navigate(buildPath({ inspectionId })),
    [buildPath, navigate],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper component="form" sx={{ p: '2px 4px', mb: 2, display: 'flex', alignItems: 'center', width: 400 }}>
        <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder={t('Project_Inspections_Search')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </Paper>

      {pagedInspections.data.map(inspection => (
        <InspectionRow
          key={inspection.info.id}
          inspection={inspection}
          projectId={projectId}
          projectName={project?.projectInfo.name ?? '-'}
          onOpen={openInspection}
        />
      ))}

      {pagedInspections.data.length === 0 && (
        <Card sx={{ border: 'none' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? t('Inspections_NotMatch') : t('Inspections_NotFound')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {pagedInspections.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pagedInspections.totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default InspectionsTab;
